import { toDecimalOddsFormat, toFractionalOddsFormat, toAmericanOddsFormat } from 'odds-utils';
import {IMaxOdd, oddsFormats, IProviderCompetition, IProvider, IWidgetProvider} from './types';
import {IOdds} from "../api/match-center";
import {OutcomeIds} from './outcome';
import {IOddsObject, IOddsRow} from "../components/MatchCenter/OddsComparisonTable/types";
import {getWidgetProviders} from './widget-providers';

export function formatOdds(odds: number | null, oddsFormat: string | undefined) : string {
  if (odds) {
    switch (oddsFormat) {
      case oddsFormats.fractional:
        return toFractionalOddsFormat(odds);

      case oddsFormats.american:
        return toAmericanOddsFormat(odds);
    }

    return toDecimalOddsFormat(parseFloat(odds.toString())); // odds can be string !
  }

  return '';
}

const findCustomProviderOutLinkByProviderSlug = (providerSlug: string): null|string => {
  const widgetProviders = getWidgetProviders();
  const widgetProvider: IWidgetProvider | undefined = widgetProviders.find((widgetProvider: IWidgetProvider) => {
    return widgetProvider?.provider?.slug === providerSlug;
  });

  return widgetProvider?.outLink ? widgetProvider.outLink : null;
}

export function getOutLink(
  outLinkFolder: string|null|undefined,
  providerSlug: string,
  widgetId: number|undefined = undefined,
  eventId: number|undefined = undefined,
  builderComponentId: number|undefined = undefined,
): string {

  const customOutLink: null|string = findCustomProviderOutLinkByProviderSlug(providerSlug);
  if (customOutLink) {
    return customOutLink;
  }

  const queryStringsArray: string[] = [];
  const urlQueryParams = {
    widgetId: widgetId,
    eventId: eventId,
    widgetSubComponentId: builderComponentId,
  }

  Object.keys(urlQueryParams).forEach(function(key) {
    if (urlQueryParams[key]) {
      queryStringsArray.push(`${key}=${urlQueryParams[key]}`)
    }
  });

  return outLinkFolder !== null
    ? `${window.location.origin}/${outLinkFolder}/${providerSlug}${(queryStringsArray.length > 0) ? '?' + queryStringsArray.join('&') : ''}`
    : '';
}

export function americanOddsToDecimal(americanOdds: number): number {
  return americanOdds > 0 ? (1 + (americanOdds/100)) : (1 - (100/americanOdds));
}

export function fractionalOddsToDecimal(fractionalOdds: string): number {
  let numbers = fractionalOdds.split('/');
  return (parseInt(numbers[0]) / parseInt(numbers[1])) + 1;
}

export function getOdds(odds: IOdds[] | null, outcomeId: number, oddsFormat: string, promotedProvider?: IProviderCompetition|null): string|null {
  if (odds === null)
    return null;
  let filteredOdds = odds.find((item) => {
    let result = item.outcomeId === outcomeId;
    if (promotedProvider !== undefined && promotedProvider !== null)
      result = result && item.providerId === promotedProvider.providerId;
    return result;
  });
  if (filteredOdds === undefined)
    return null;

  return formatOdds(filteredOdds.odds, oddsFormat);
}

export const findOddsByOutcomeId = (odds: IOdds[], outcomeId: OutcomeIds, promotedProvider: IProviderCompetition | null): IOdds | null => {
  return odds.find((oddsItem: IOdds) => {
    let result = oddsItem.outcomeId === outcomeId;
    if (promotedProvider !== null)
      result = result && oddsItem.providerId === promotedProvider.providerId;

    return result;
  }) ?? null
}

export function getOddsDataFromMax(odds:  object | IMaxOdd, outcome: string, oddsFormat: string): [string, 'down'|'up'|null] | null {

  if(odds["outcomeName"] === outcome){
    var maxOdds = odds["odds"]
    var maxPrevOdds = odds["prevOdds"]
    return [formatOdds(maxOdds, oddsFormat), differenceChecker(maxOdds,maxPrevOdds)]}

  return  null
}

export function getOddsDirection(odds: IOdds[] | null, outcomeId?: number|undefined): 'down'|'up'|null {
  if (odds === null)
    return null;

  let filteredOdds: IOdds | undefined;

  if (outcomeId !== undefined)
    filteredOdds = odds.find((item) => item.outcomeId === outcomeId);
  if (filteredOdds === undefined || !filteredOdds.oddsPrev)
    return null;

  return filteredOdds.odds > filteredOdds.oddsPrev ? 'up' : 'down';
}

export function differenceChecker(num1 : number, num2 : number, isOdds: boolean = false): 'down'|'up'|null {
  return num1 > num2 ? 'up' : 'down';
}

// Returns one (or more) the best Odds from Odds array
// If there now best Odds returns empty array
export const findBestOddsFromOddsArray = (odds: IOdds[]): IOdds[] => {
  let maxOdds: number = 0;
  odds.forEach((oddsItem: IOdds) => {
    if (oddsItem.odds > maxOdds) {
      maxOdds = oddsItem.odds;
    }
  })

  const bestOdds = odds.filter((oddsItem: IOdds) => oddsItem.odds === maxOdds);
  return bestOdds.length === odds.length ? [] : bestOdds;
}

export const getPromotedProviderOdds = (odds: IOdds[], promotedProvider: IProviderCompetition | null): IOdds | null => {
  if (promotedProvider === null)
    return null;
  let promotedOdds = odds.find((item) => {
    return item.providerId === promotedProvider.providerId;
  });

  if (promotedOdds === undefined)
    return null;

  return promotedOdds;
}

export function sortOddsByPromotedProvider(odds: { oddsJson: IOdds }[] | null, promotedProvider: IProviderCompetition | null): IOddsRow[]
export function sortOddsByPromotedProvider(odds: IOddsRow[] | null, promotedProvider: IProviderCompetition | null): IOddsRow[]
export function sortOddsByPromotedProvider(odds: IOddsObject[] | null, promotedProvider: IProviderCompetition | null): IOddsObject[]
export function sortOddsByPromotedProvider(odds: IOdds[] | null, promotedProvider: IProviderCompetition | null): IOdds[]
export function sortOddsByPromotedProvider(odds: any[] | null, promotedProvider: IProviderCompetition | null): any[] {
  if (odds === null)
    return [];
  if (promotedProvider === null)
    return odds;

  return odds.sort((a: any, b: any) => {
    let aProviderId = null
    let bProviderId = null
    if ('providerId' in a && 'providerId' in b) {
      aProviderId = a.providerId
      bProviderId = b.providerId
    } else if (('provider' in a && a.provider !== undefined) && ('provider' in b && b.provider !== undefined)) {
      aProviderId = a.provider.id
      bProviderId = b.provider.id
    } else return 0

    if (aProviderId === promotedProvider.providerId)
      return -1
    else if (bProviderId === promotedProvider.providerId)
      return 1

    return 0;
  });
}

export function sortProvidersByPromotedProvider(providers: IProvider[], promotedProvider: IProviderCompetition | null): IProvider[] {
  return providers.sort((a, b) => {
    if (promotedProvider === null)
      return 0;
    if (a.id === promotedProvider.providerId)
      return -1;
    else if (b.id === promotedProvider.providerId)
      return 1;

    return 0;
  })
}

//Checks if all 3 outcomes are available and odds are not null
export function isItalianOddsAvailable(odds: IMaxOdd[]): boolean {
  let result = true
  odds.forEach((item) => {
    if (item.odds === null)
      result = false
  })

  return result
}
